export interface TourStep {
  /** data-tour attribute value to find the target element */
  target: string;
  /** i18n key for the step title */
  title: string;
  /** i18n key for the step content */
  content: string;
  /** Tooltip placement relative to the target */
  placement: "top" | "bottom" | "left" | "right";
  /** Helper name to call before showing this step (e.g. open a panel) */
  beforeAction?: string;
  /** Extra padding around the spotlight cutout (px) */
  highlightPadding?: number;
  /** If true, show challenge mode (user must do the action) */
  challengeMode?: boolean;
  /** Helper name returning boolean — polled to check challenge completion */
  challengeCheck?: string;
}

// ─── Pipeline Builder Tour (14 steps) ──────────────────────────────────────

export const pipelineTourSteps: TourStep[] = [
  {
    target: "pipeline-welcome",
    title: "tour.pipeline.step1.title",
    content: "tour.pipeline.step1.content",
    placement: "bottom",
    highlightPadding: 0,
  },
  {
    target: "pipeline-step-palette",
    title: "tour.pipeline.step2.title",
    content: "tour.pipeline.step2.content",
    placement: "right",
  },
  {
    target: "pipeline-canvas",
    title: "tour.pipeline.step3.title",
    content: "tour.pipeline.step3.content",
    placement: "left",
    highlightPadding: 8,
  },
  {
    target: "pipeline-name-input",
    title: "tour.pipeline.step4.title",
    content: "tour.pipeline.step4.content",
    placement: "bottom",
    challengeMode: true,
    challengeCheck: "hasName",
  },
  {
    target: "pipeline-step-palette",
    title: "tour.pipeline.step5.title",
    content: "tour.pipeline.step5.content",
    placement: "right",
    challengeMode: true,
    challengeCheck: "hasStep",
  },
  {
    target: "pipeline-canvas",
    title: "tour.pipeline.step6.title",
    content: "tour.pipeline.step6.content",
    placement: "left",
    challengeMode: true,
    challengeCheck: "hasSelectedStep",
  },
  {
    target: "pipeline-step-editor",
    title: "tour.pipeline.step7.title",
    content: "tour.pipeline.step7.content",
    placement: "left",
    beforeAction: "openStepEditor",
  },
  {
    target: "pipeline-step-palette",
    title: "tour.pipeline.step8.title",
    content: "tour.pipeline.step8.content",
    placement: "right",
    challengeMode: true,
    challengeCheck: "hasSecondStep",
  },
  {
    target: "pipeline-canvas",
    title: "tour.pipeline.step9.title",
    content: "tour.pipeline.step9.content",
    placement: "left",
    challengeMode: true,
    challengeCheck: "hasConnection",
  },
  {
    target: "pipeline-transitions-panel",
    title: "tour.pipeline.step10.title",
    content: "tour.pipeline.step10.content",
    placement: "left",
    beforeAction: "openStepEditor",
  },
  {
    target: "pipeline-undo-redo",
    title: "tour.pipeline.step11.title",
    content: "tour.pipeline.step11.content",
    placement: "top",
  },
  {
    target: "pipeline-save-btn",
    title: "tour.pipeline.step12.title",
    content: "tour.pipeline.step12.content",
    placement: "bottom",
  },
  {
    target: "pipeline-publish-btn",
    title: "tour.pipeline.step13.title",
    content: "tour.pipeline.step13.content",
    placement: "bottom",
  },
  {
    target: "pipeline-welcome",
    title: "tour.pipeline.step14.title",
    content: "tour.pipeline.step14.content",
    placement: "bottom",
    highlightPadding: 0,
  },
];

// ─── Guided Flow Builder Tour (16 steps) ───────────────────────────────────

export const flowTourSteps: TourStep[] = [
  {
    target: "flow-welcome",
    title: "tour.flow.step1.title",
    content: "tour.flow.step1.content",
    placement: "bottom",
    highlightPadding: 0,
  },
  {
    target: "flow-add-step-btn",
    title: "tour.flow.step2.title",
    content: "tour.flow.step2.content",
    placement: "top",
  },
  {
    target: "flow-canvas",
    title: "tour.flow.step3.title",
    content: "tour.flow.step3.content",
    placement: "left",
    highlightPadding: 8,
  },
  {
    target: "flow-name-input",
    title: "tour.flow.step4.title",
    content: "tour.flow.step4.content",
    placement: "bottom",
    challengeMode: true,
    challengeCheck: "hasName",
  },
  {
    target: "flow-agent-selector",
    title: "tour.flow.step5.title",
    content: "tour.flow.step5.content",
    placement: "right",
    beforeAction: "openSettingsTab",
    challengeMode: true,
    challengeCheck: "hasAgent",
  },
  {
    target: "flow-step-palette",
    title: "tour.flow.step6.title",
    content: "tour.flow.step6.content",
    placement: "right",
    beforeAction: "openPaletteTab",
    challengeMode: true,
    challengeCheck: "hasStep",
  },
  {
    target: "flow-canvas",
    title: "tour.flow.step7.title",
    content: "tour.flow.step7.content",
    placement: "left",
    challengeMode: true,
    challengeCheck: "hasSelectedStep",
  },
  {
    target: "flow-step-editor",
    title: "tour.flow.step8.title",
    content: "tour.flow.step8.content",
    placement: "left",
    beforeAction: "openStepEditor",
  },
  {
    target: "flow-step-editor",
    title: "tour.flow.step9.title",
    content: "tour.flow.step9.content",
    placement: "left",
    beforeAction: "openStepEditor",
  },
  {
    target: "flow-step-palette",
    title: "tour.flow.step10.title",
    content: "tour.flow.step10.content",
    placement: "right",
    beforeAction: "openPaletteTab",
    challengeMode: true,
    challengeCheck: "hasSecondStep",
  },
  {
    target: "flow-canvas",
    title: "tour.flow.step11.title",
    content: "tour.flow.step11.content",
    placement: "left",
    challengeMode: true,
    challengeCheck: "hasConnection",
  },
  {
    target: "flow-transitions-panel",
    title: "tour.flow.step12.title",
    content: "tour.flow.step12.content",
    placement: "left",
    beforeAction: "openStepEditor",
  },
  {
    target: "flow-undo-redo",
    title: "tour.flow.step13.title",
    content: "tour.flow.step13.content",
    placement: "top",
  },
  {
    target: "flow-save-indicator",
    title: "tour.flow.step14.title",
    content: "tour.flow.step14.content",
    placement: "bottom",
  },
  {
    target: "flow-publish-btn",
    title: "tour.flow.step15.title",
    content: "tour.flow.step15.content",
    placement: "bottom",
  },
  {
    target: "flow-welcome",
    title: "tour.flow.step16.title",
    content: "tour.flow.step16.content",
    placement: "bottom",
    highlightPadding: 0,
  },
];

// ─── Router Config Tour (8 steps) ────────────────────────────────────────

export const routerTourSteps: TourStep[] = [
  {
    target: "router-welcome",
    title: "tour.router.step1.title",
    content: "tour.router.step1.content",
    placement: "bottom",
    highlightPadding: 0,
  },
  {
    target: "router-tabs",
    title: "tour.router.step2.title",
    content: "tour.router.step2.content",
    placement: "bottom",
  },
  {
    target: "router-general-tab",
    title: "tour.router.step3.title",
    content: "tour.router.step3.content",
    placement: "bottom",
    beforeAction: "goToTab0",
  },
  {
    target: "router-strategy-tab",
    title: "tour.router.step4.title",
    content: "tour.router.step4.content",
    placement: "bottom",
    beforeAction: "goToTab1",
  },
  {
    target: "router-routes-tab",
    title: "tour.router.step5.title",
    content: "tour.router.step5.content",
    placement: "bottom",
    beforeAction: "goToTab2",
  },
  {
    target: "router-channels-tab",
    title: "tour.router.step6.title",
    content: "tour.router.step6.content",
    placement: "bottom",
    beforeAction: "goToTab3",
  },
  {
    target: "router-save-btn",
    title: "tour.router.step7.title",
    content: "tour.router.step7.content",
    placement: "bottom",
  },
  {
    target: "router-welcome",
    title: "tour.router.step8.title",
    content: "tour.router.step8.content",
    placement: "bottom",
    highlightPadding: 0,
  },
];

/** Lookup the steps array for a tour name */
export function getTourSteps(tourName: "pipeline" | "flow" | "router"): TourStep[] {
  if (tourName === "pipeline") return pipelineTourSteps;
  if (tourName === "flow") return flowTourSteps;
  return routerTourSteps;
}
